import { useEffect, useRef, useState } from "react";
import { ChatMessage, ChatDataProps } from "../types/backend/SengiLabsTypes";
import { ApiPortalBackend, PortalConversation, PortalMessage, PortalMessageContentPart } from "../apis/ApiPortalBackend";
import { UseChatHistoryOptions } from "../types/chat";
import { isPersistedConversationId, toBackendContent, toChatMessage } from "../utils/chat/runtimeChat";

//TODO: Load Chat Massages for user from database
export const useChatHistory = (chatID:string,options: UseChatHistoryOptions = {}) : [chatMessages:ChatMessage[], addMessage:(entry:ChatMessage)=>Promise<string | null>, addMessages:(entries:ChatMessage[])=>void, getLastMessage:()=>ChatMessage | undefined,reloadMessages: () => Promise<void>, appendLocalMessage:(conversationId: string, entry: ChatMessage) => void] => {
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const conversationIdRef = useRef<string | null>(isPersistedConversationId(chatID) ? chatID : null);
    const creatingConversationRef = useRef<Promise<PortalConversation | null> | null>(null); 
    const notifiedConversationRef = useRef<string | null>(null);

    const ensureConversation = async (): Promise<PortalConversation | null> => {
        if (conversationIdRef.current) return null;
        if (!creatingConversationRef.current) {
            creatingConversationRef.current =
                (async () => {
                    const workspaceId = await ApiPortalBackend .GetDefaultWorkspaceId();
                    if (!workspaceId)  return null; 

                    let conversation = await ApiPortalBackend .CreateConversation(workspaceId, 'chat');
                    if (!conversation)  return null;

                    const title =options.defaultTitle?.trim();
                    if (title) {
                        const updated =await ApiPortalBackend.UpdateConversation(conversation.id, { title });
                        if (updated) {
                            conversation = updated;
                        }
                    }
                    conversationIdRef.current = conversation.id;
                    return conversation;
                })();
        }
        const conversation = await creatingConversationRef.current;
        creatingConversationRef.current = null;

        return conversation;
    };
    const persistMessage = async (entry: ChatMessage): Promise<string | null> => {
        try {
            let conversationId = conversationIdRef.current;
            let createdConversation: PortalConversation | null = null;
            if (!conversationId) {
                createdConversation = await ensureConversation();
                conversationId = conversationIdRef.current;
            }
            if (!conversationId) return null;

            const { content, contentParts } = toBackendContent(entry);
            await ApiPortalBackend.PostConversationMessage(conversationId, entry.role, content, contentParts);

            if (createdConversation && notifiedConversationRef.current !== createdConversation.id) {
                notifiedConversationRef.current = createdConversation.id;
                options.onConversationCreated?.(createdConversation);
            }
            return conversationId;
        } catch (error) {
            console.warn('Failed to persist chat message:', error);
            return null;
        }
    };
    const reloadMessages = async () => {
        const conversationId = conversationIdRef.current;
        if (!conversationId) return;
        await loadPersistedMessages(conversationId);
    };

    async function addMessage(entry:ChatMessage): Promise<string | null> {
        setChatMessages(prev => [ ...prev, entry ]);
        return await persistMessage(entry);
    }

    function addMessages(entries:ChatMessage[]) {
        setChatMessages(prev => [ ...prev, ...entries ]);
        entries.forEach(persistMessage);
    }

    // Nur lokal anzeigen (nicht persistieren) - fuer bereits anderweitig gespeicherte Nachrichten, z. B.
    // die fertig gestreamte Antwort, damit sie bis zum Reload nicht kurz verschwindet. Greift nur,
    // wenn die Conversation gerade angezeigt wird.
    function appendLocalMessage(conversationId: string, entry: ChatMessage) {
        if (conversationIdRef.current !== conversationId) return;
        setChatMessages(prev => [ ...prev, entry ]);
    }

    function getLastMessage() {
        return chatMessages[chatMessages.length-1];
    }

    const loadPersistedMessages = async (conversationId: string) => {
        const messages = await ApiPortalBackend.GetConversationMessages(conversationId);
        setChatMessages(messages ? messages.map(toChatMessage) : []);
    };

    useEffect(() => {
        if (isPersistedConversationId(chatID)) {
            conversationIdRef.current = chatID;
            loadPersistedMessages(chatID);
        }
        else {
            conversationIdRef.current = null;
            setChatMessages([]);
        }
    }, [chatID]);

    return [chatMessages, addMessage, addMessages, getLastMessage,reloadMessages, appendLocalMessage];
}
